import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import logo from '../../logo2.png';
import { useAuth } from '../../contexts/AuthContext';

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin }) => {
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      await register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        password_confirm: formData.confirmPassword,
        first_name: formData.first_name,
        last_name: formData.last_name,
      });
    } catch (err: any) {
      if (err.response?.data?.detail) {
        setError(typeof err.response.data.detail === 'string' 
          ? err.response.data.detail 
          : JSON.stringify(err.response.data.detail)
        );
      } else {
        setError('Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 5, 
        maxWidth: 520, 
        mx: 'auto',
        borderRadius: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box 
          component="img"
          src={logo}
          alt="YVB Logo"
          sx={{ 
            height: '48px',
            mb: 2,
          }}
        />
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            background: 'linear-gradient(45deg, #2563eb, #059669)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Join YVB
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          Create your professional veterinary account
        </Typography>
      </Box>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontWeight: 500,
            },
          }}
        >
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
          <TextField
            fullWidth
            label="First Name"
            value={formData.first_name}
            onChange={handleChange('first_name')}
            required
            sx={{
              '& .MuiInputLabel-root': {
                fontWeight: 500,
              },
            }}
          />
          <TextField
            fullWidth
            label="Last Name"
            value={formData.last_name}
            onChange={handleChange('last_name')}
            required
            sx={{
              '& .MuiInputLabel-root': {
                fontWeight: 500,
              },
            }}
          />
        </Box>

        <TextField
          fullWidth
          label="Username"
          value={formData.username}
          onChange={handleChange('username')}
          margin="normal"
          required
          helperText="Choose a unique username for your account"
          sx={{
            mb: 2.5,
            '& .MuiInputLabel-root': {
              fontWeight: 500,
            },
            '& .MuiFormHelperText-root': {
              fontSize: '0.8125rem',
              fontWeight: 400,
            },
          }}
        />

        <TextField
          fullWidth
          type="email"
          label="Professional Email"
          value={formData.email}
          onChange={handleChange('email')}
          margin="normal"
          required
          sx={{
            mb: 2.5,
            '& .MuiInputLabel-root': {
              fontWeight: 500,
            },
          }}
        />
        
        <TextField
          fullWidth
          type="password"
          label="Password"
          value={formData.password}
          onChange={handleChange('password')}
          margin="normal"
          required
          helperText="Minimum 8 characters, include letters and numbers"
          sx={{
            mb: 2.5,
            '& .MuiInputLabel-root': {
              fontWeight: 500,
            },
            '& .MuiFormHelperText-root': {
              fontSize: '0.8125rem',
              fontWeight: 400,
            },
          }}
        />

        <TextField
          fullWidth
          type="password"
          label="Confirm Password"
          value={formData.confirmPassword}
          onChange={handleChange('confirmPassword')}
          margin="normal"
          required
          sx={{
            mb: 3,
            '& .MuiInputLabel-root': {
              fontWeight: 500,
            },
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ 
            mt: 1,
            mb: 3,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 2,
            background: 'linear-gradient(45deg, #059669, #10b981)',
            boxShadow: '0 4px 6px -1px rgb(5 150 105 / 0.2)',
            '&:hover': {
              background: 'linear-gradient(45deg, #047857, #059669)',
              boxShadow: '0 10px 15px -3px rgb(5 150 105 / 0.3)',
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              background: 'linear-gradient(45deg, #94a3b8, #cbd5e1)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} sx={{ color: 'white' }} />
              <span>Creating account...</span>
            </Box>
          ) : (
            'Create Account'
          )}
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Already have an account?{' '}
            <Link
              component="button"
              type="button"
              onClick={onSwitchToLogin}
              underline="hover"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                '&:hover': {
                  color: 'primary.dark',
                },
              }}
            >
              Sign in here
            </Link>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default RegisterForm; 